import { baseApi } from "./baseApi";

const E_BILL_PROVIDER_BASE_URL = "/ebill-provider";

export const eBillProviderApi = baseApi.injectEndpoints({
  tagTypes: ["EBillProvider"],
  endpoints: (build) => ({
    // CREATE
    addEBillProvider: build.mutation({
      query: (body) => ({
        url: E_BILL_PROVIDER_BASE_URL,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "EBillProvider", id: "LIST" }],
    }),

    // LIST
    listEBillProviders: build.query({
      query: (params) => ({
        url: E_BILL_PROVIDER_BASE_URL,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "EBillProvider",
                id: _id,
              })),
              { type: "EBillProvider", id: "LIST" },
            ]
          : [{ type: "EBillProvider", id: "LIST" }],
    }),

    // DDL
    getDDLEBillProviderList: build.query({
      query: (params) => ({
        url: `${E_BILL_PROVIDER_BASE_URL}/ddl`,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: "EBillProvider",
                id: _id,
              })),
              { type: "EBillProvider", id: "DDL" },
            ]
          : [{ type: "EBillProvider", id: "DDL" }],
    }),

    // DETAIL
    getEBillProviderDetail: build.query({
      query: (id) => `${E_BILL_PROVIDER_BASE_URL}/${id}`,
      providesTags: (result, error, id) => [
        { type: "EBillProvider", id },
        { type: "EBillProvider", id: "LIST" },
      ],
    }),

    // UPDATE
    updateEBillProvider: build.mutation({
      query: ({ _id, ...body }) => ({
        url: `${E_BILL_PROVIDER_BASE_URL}/${_id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { _id: id }) => [
        { type: "EBillProvider", id },
        { type: "EBillProvider", id: "LIST" },
      ],
    }),

    createEndpoints: build.mutation({
      query: ({ id, ...body }) => ({
        url: `${E_BILL_PROVIDER_BASE_URL}/${id}/endpoints`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { _id }) => [
        { type: "E-BillProvider", id: _id },
        { type: "E-BillProvider", id: "LIST" },
      ],
    }),

    listEndpoints: build.query({
      query: (id) => `${E_BILL_PROVIDER_BASE_URL}/${id}/list-endpoints`,
      providesTags: (result, error, { _id }) => [
        { type: "E-BillProvider", id: _id },
        { type: "E-BillProvider", id: "LIST" },
      ],
    }),

    deleteEndpoint: build.mutation({
      query: ({ id, name }) => ({
        url: `${E_BILL_PROVIDER_BASE_URL}/${id}/endpoints/${encodeURIComponent(
          name,
        )}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "E-BillProvider", id },
        { type: "E-BillProvider", id: "LIST" },
      ],
    }),

    createCredentials: build.mutation({
      query: ({ id, entityId, ...body }) => ({
        url: `${E_BILL_PROVIDER_BASE_URL}/${id}/credentials`,
        method: "PUT",
        body: entityId ? { ...body, entityId } : body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "E-BillProvider", id: arg?.id || arg?._id },
        { type: "E-BillProvider", id: "LIST" },
      ],
    }),

    listCredentials: build.query({
      query: (args) => {
        if (typeof args === "string") {
          return `${E_BILL_PROVIDER_BASE_URL}/${args}/list-credentials`;
        }

        const { id, entityId } = args || {};
        return {
          url: `${E_BILL_PROVIDER_BASE_URL}/${id}/list-credentials`,
          params: entityId ? { entityId } : undefined,
        };
      },
      providesTags: (result, error, arg) => [
        { type: "E-BillProvider", id: arg?.id || arg },
        { type: "E-BillProvider", id: "LIST" },
      ],
    }),

    deleteCredential: build.mutation({
      query: ({ id, key, entityId }) => ({
        url: `${E_BILL_PROVIDER_BASE_URL}/${id}/credentials/${key}`,
        method: "DELETE",
        params: entityId ? { entityId } : undefined,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "E-BillProvider", id: arg?.id },
        { type: "E-BillProvider", id: "LIST" },
      ],
    }),
    getEntityEBillProviderDDL: build.query({
      query: (params) => ({
        url: `${E_BILL_PROVIDER_BASE_URL}/entity-eBill-ddl`,
        params,
      }),
      providesTags: [{ type: "E-BillProvider", id: "DDL" }],
    }),

    useEBillProvider: build.mutation({
      query: (args) => {
        if (typeof args === "string") {
          return {
            url: `${E_BILL_PROVIDER_BASE_URL}/${args}/use`,
            method: "PUT",
          };
        }

        const { providerId, entityId } = args || {};
        return {
          url: `${E_BILL_PROVIDER_BASE_URL}/${providerId}/use`,
          method: "PUT",
          body: entityId ? { entityId } : undefined,
        };
      },
      invalidatesTags: [{ type: "E-BillProvider", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useAddEBillProviderMutation,
  useListEBillProvidersQuery,
  useLazyListEBillProvidersQuery,
  useLazyGetDDLEBillProviderListQuery,
  useGetEBillProviderDetailQuery,
  useUpdateEBillProviderMutation,
  useCreateEndpointsMutation,
  useListEndpointsQuery,
  useDeleteEndpointMutation,
  useCreateCredentialsMutation,
  useListCredentialsQuery,
  useLazyListCredentialsQuery,
  useDeleteCredentialMutation,
  useGetEntityEBillProviderDDLQuery,
  useUseEBillProviderMutation,
} = eBillProviderApi;
